import React, { useEffect, useState } from 'react';
import { BaseControlProps } from '../../models/BaseControlProps';
import { ReactComponent as SvgLogo } from "../../components/Header/logo.svg";

import './ValidateIdentity.css';
import ReCAPTCHA from 'react-google-recaptcha';
import { AuthAPIService } from '../../services/AuthAPIServices';
import { UtilService } from '../../services/UtilService';
import { AuthUserValidateIdentityRequest } from '../../models/AuthUserValidateIdentityRequest';
import { useNavigate } from 'react-router-dom';
import MuiCard from '@mui/material/Card';
import { styled } from '@mui/material/styles';
import Stack from '@mui/material/Stack';
import { Box, Button, CircularProgress, FormControl, FormHelperText, FormLabel, TextField, Typography } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers';
import dayjs, { Dayjs } from 'dayjs';
import { MessageInfo } from '../../models/MessageInfo';
import MessageDialog from '../../components/Common/MessageDialog/MessageDialog';

//type DatePickerChangeHandler = (date: DatePickerProps['value'], dateString: string) => void;
type ReCaptchaVerifyCallback = (token: string) => void;

const Card = styled(MuiCard)(({ theme }) => ({
    display: 'flex',
    flexDirection: 'column',
    alignSelf: 'center',
    width: '100%',
    padding: theme.spacing(4),
    gap: theme.spacing(2),
    margin: 'auto',
    boxShadow:
      'hsla(220, 30%, 5%, 0.05) 0px 5px 15px 0px, hsla(220, 25%, 10%, 0.05) 0px 15px 35px -5px',
    [theme.breakpoints.up('sm')]: {
      width: '550px',
    },
    ...theme.applyStyles('dark', {
      boxShadow:
        'hsla(220, 30%, 5%, 0.5) 0px 5px 15px 0px, hsla(220, 25%, 10%, 0.08) 0px 15px 35px -5px',
    }),
  }));

const ValidateContainer = styled(Stack)(({ theme }) => ({
    padding: 10,
    marginTop: '5vh',
    '&::before': {
      content: '""',
      display: 'block',
      position: 'absolute',
      zIndex: -1,
      inset: 0
    },
  }));

const ValidateIdentity = (props: BaseControlProps) => {
    const [scope,apply] = React.useState<AuthUserValidateIdentityRequest>({
        addressLine1: "",
        ibanLast4: "",
        lastNetPayment: "",
        dob: null,
        ppsNumber: "",
        personnelRefNo: ""
    });
    const [isCaptchaVerified, setIsCaptchaVerified] = useState(false);
    const [captchError, isCaptchaError] = useState(false);
    const maxAllowedDate = dayjs().subtract(18, 'years');
    const [addressLine1Error, setAddressLine1Error] = React.useState(false);
    const [addressLine1ErrorMessage, setAddressLine1ErrorMessage] = React.useState('');
    const [ibanLast4Error, setIbanLast4Error] = React.useState(false);
    const [ibanLast4ErrorMessage, setIbanLast4ErrorMessage] = React.useState('');
    const [lastNetPaymentError, setLastNetPaymentError] = React.useState(false);
    const [lastNetPaymentErrorMessage, setLastNetPaymentErrorMessage] = React.useState('');
    const [dobError, setDobError] = React.useState(false);
    const [dobErrorMessage, setDobErrorMessage] = React.useState('');
    const [ppsNumberError, setPpsNumberError] = React.useState(false);
    const [ppsNumberErrorMessage, setPpsNumberErrorMessage] = React.useState('');
    const [personnelRefNoError, setPersonnelRefNoError] = React.useState(false);
    const [personnelRefNoErrorMessage, setPersonnelRefNoErrorMessage] = React.useState('');
    const [inputValid, isInputValid] = useState(false);
    const [loading, setLoading] = useState(false);
    const authService = new AuthAPIService();
    const navigate = useNavigate();
    const [messageInfo, setMessageInfo] = useState<MessageInfo | null>(null);
    const [showMessage, setShowMessage] = useState(false);
    
    useEffect(() => {
        localStorage.removeItem('peopleXDId');
    }, [])
    
    function applyScope(){
        apply(UtilService.cloneObject(scope));
    }

    const handleCaptchaChange = (value: string | null) => {
        if (value) {
          setIsCaptchaVerified(true);
        } else {
          setIsCaptchaVerified(false);
        }
      };

    const onChangeDob = (date: Dayjs | null) => {
        scope.dob = date ? date?.toISOString() : null;
        applyScope();
    };

    const validateIdentity = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();

        if (!inputValid) { return; }
        setLoading(true);

        try {
            const response = await authService.validateIdentity(scope);
            setLoading(false);
    
            if(response.error){
                    setMessageInfo(UtilService.getErrorMessageInfo("We couldn't validate your information. Please reach out to Mercer for assistance."));
                    setShowMessage(true);
                return;
            }
            else{
                if(response.response?.isValid){
                    localStorage.setItem('peopleXDId', response.response?.encryptedPeopleXDId);
                    navigate('/register');
                }
                else{
                    setMessageInfo(UtilService.getErrorMessageInfo("We couldn't validate your information. Please reach out to Mercer for assistance."));
                    setShowMessage(true);
                }
            }

        } catch (error) {
            setLoading(false);
            setMessageInfo(UtilService.getErrorMessageInfo("We couldn't validate your information. Please reach out to Mercer for assistance."));
            setShowMessage(true);
        }
    }

    const validateInputs = () => {

        const addressLine1 = document.getElementById('addressLine1') as HTMLInputElement;
        scope.addressLine1 = addressLine1.value;
        const ibanLast4 = document.getElementById('ibanLast4') as HTMLInputElement;
        scope.ibanLast4 = ibanLast4.value;
        const lastNetPayment = document.getElementById('lastNetPayment') as HTMLInputElement;
        scope.lastNetPayment = lastNetPayment.value;
        const ppsNumber = document.getElementById('ppsNumber') as HTMLInputElement;
        scope.ppsNumber = ppsNumber.value;
        const personnelRefNo = document.getElementById('personnelRefNo') as HTMLInputElement;
        scope.personnelRefNo = personnelRefNo.value;
        applyScope();
        let validFieldCount = 0;
    
        if (!addressLine1.value || addressLine1.value.length < 1) {
          setAddressLine1Error(true);
          setAddressLine1ErrorMessage('Please enter a valid Address Line 1.');
        } else {
          setAddressLine1Error(false);
          setAddressLine1ErrorMessage('');
          validFieldCount++;
        }
    
        if (!ibanLast4.value || ibanLast4.value.length !== 4) {
          setIbanLast4Error(true);
          setIbanLast4ErrorMessage('Please enter IBAN last 4 Digits.');
        } else {
          setIbanLast4Error(false);
          setIbanLast4ErrorMessage('');
          validFieldCount++;
        }
    
        if (!lastNetPayment.value || lastNetPayment.value.length < 1) {
          setLastNetPaymentError(true);
          setLastNetPaymentErrorMessage('Please enter last Net Payment.');
        } else {
          setLastNetPaymentError(false);
          setLastNetPaymentErrorMessage('');
          validFieldCount++;
        }

        if (!scope.dob) {
            setDobError(true);
            setDobErrorMessage('Please select Date of Birth.');
        } else {
            setDobError(false);
            setDobErrorMessage('');
            validFieldCount++;
        }

        if (!ppsNumber.value || ppsNumber.value.length < 1) {
            setPpsNumberError(true);
            setPpsNumberErrorMessage('Please enter PPS Number.');
        } else {
            setPpsNumberError(false);
            setPpsNumberErrorMessage('');
            validFieldCount++;
        }

        if (!personnelRefNo.value || personnelRefNo.value.length < 1) {
            setPersonnelRefNoError(true);
            setPersonnelRefNoErrorMessage('Please enter Personnel Reference Number.');
        } else {
            setPersonnelRefNoError(false);
            setPersonnelRefNoErrorMessage('');
            validFieldCount++;
        }
        
        if (!isCaptchaVerified) {
            isCaptchaError(true);
        } else {
            isCaptchaError(false);
        }

        const isValid = validFieldCount >= 4 && isCaptchaVerified;
        
        isInputValid(isValid);

        if (isValid) {
            setAddressLine1Error(false);
            setAddressLine1ErrorMessage('');
            setIbanLast4Error(false);
            setIbanLast4ErrorMessage('');
            setLastNetPaymentError(false);
            setLastNetPaymentErrorMessage('');
            setDobError(false);
            setDobErrorMessage('');
            setPpsNumberError(false);
            setPpsNumberErrorMessage('');
            setPersonnelRefNoError(false);
            setPersonnelRefNoErrorMessage('');
        }

        return isValid;
      };

    return(
        <>
            <ValidateContainer direction="column" justifyContent="space-between">
                    <Card variant="outlined">
                        <div className="logo-container">
                            <SvgLogo focusable={false} viewBox="0 0 500 150" />
                        </div>
                        <Typography
                            component="h1"
                            variant="h6"
                            sx={{ width: '100%', fontSize: 'clamp(2rem, 10vw, 2.15rem)' }}
                        >
                            Validate My Identity
                        </Typography>
                        <Typography
                            component="h2"
                            variant="subtitle2"
                        >
                            Please note: You must answer at least 4 out of the 6 questions to proceed with your registration.
                        </Typography>
                        <Box
                            component="form"
                            onSubmit={validateIdentity}
                            sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}
                        >
                            <FormControl>
                                <FormLabel htmlFor="personnelRefNo">Personal Reference Number: <i>(MEPP number)</i></FormLabel>
                                <TextField
                                    fullWidth
                                    id="personnelRefNo"
                                    placeholder="Personnel Reference Number"
                                    name="personnelRefNo"
                                    variant="outlined"
                                    error={personnelRefNoError}
                                    helperText={personnelRefNoErrorMessage}
                                    color={personnelRefNoError ? 'error' : 'primary'} 
                                ></TextField>
                            </FormControl>
                            <FormControl>
                                <FormLabel htmlFor="ppsNumber">PPS Number:</FormLabel>
                                <TextField
                                    fullWidth
                                    id="ppsNumber"
                                    placeholder="PPS Number"
                                    name="ppsNumber"
                                    variant="outlined"
                                    error={ppsNumberError}
                                    helperText={ppsNumberErrorMessage}
                                    color={ppsNumberError ? 'error' : 'primary'}
                                ></TextField>
                            </FormControl>
                            <FormControl>
                                <FormLabel htmlFor="dob-input" id="dobLabel">Date of Birth:</FormLabel>
                                <DatePicker
                                    maxDate={dayjs()} 
                                    name="dob"
                                    onChange={onChangeDob}
                                    aria-labelledby="dobLabel"
                                    slotProps={{ 
                                        textField: {
                                            helperText: dobErrorMessage,
                                            error: dobError,
                                            'aria-describedby': dobError ? 'dobError' : undefined,
                                            'aria-label': 'Select date of birth in DD/MM/YYYY format',
                                            placeholder: 'Date of  Birth',
                                        }
                                    }} 
                                />
                            </FormControl>
                            <FormControl>
                                <FormLabel htmlFor="ibanLast4">Last 4 Digits of Your IBAN:</FormLabel>
                                <TextField
                                    fullWidth
                                    type="number"
                                    id="ibanLast4"
                                    placeholder="IBAN"
                                    name="ibanLast4"
                                    variant="outlined"
                                    error={ibanLast4Error}
                                    helperText={ibanLast4ErrorMessage}
                                    color={ibanLast4Error ? 'error' : 'primary' }
                                    onChange={(e) => {
                                        // Client-side validation
                                        const value = e.target.value;
                                        if (value.length > 4) {
                                            e.target.value = value.slice(0, 4); // Trim to 4 digits
                                        }
                                    }}
                                />
                            </FormControl>
                            <FormControl>
                                <FormLabel htmlFor="lastNetPayment">Last Net Payment:</FormLabel>
                                <TextField
                                    fullWidth
                                    type="number"
                                    id="lastNetPayment"
                                    placeholder="Last Net Payment"
                                    name="lastNetPayment"
                                    variant="outlined"
                                    error={lastNetPaymentError}
                                    helperText={lastNetPaymentErrorMessage}
                                    color={lastNetPaymentError ? 'error' : 'primary' }
                                    inputProps={{
                                        step: "0.01",  // Ensures 2 decimal places
                                        min: "0",      // Prevents negative numbers
                                        inputMode: "decimal",
                                    }}
                                    onChange={(e) => {
                                        const value = e.target.value;
                                        // Validate 2 decimal places
                                        if (value.includes('.') && value.split('.')[1]?.length > 2) {
                                        e.target.value = parseFloat(value).toFixed(2);
                                        // Optionally set error state:
                                        setLastNetPaymentError(true);
                                        setLastNetPaymentErrorMessage("Maximum 2 decimal places allowed");
                                        }
                                    }}
                                ></TextField>
                            </FormControl>
                            <FormControl>
                                <FormLabel htmlFor="addressLine1">First Line of Address: <i>(as it appears on your payslip)</i></FormLabel>
                                <TextField 
                                    autoComplete="addressLine1"
                                    name="addressLine1"
                                    fullWidth
                                    id="addressLine1"
                                    placeholder="Address Line 1"
                                    error={addressLine1Error}
                                    helperText={addressLine1ErrorMessage}
                                    color={addressLine1Error ? 'error' : 'primary' }
                                />
                            </FormControl>
                            <FormControl error={captchError}>
                                {/* Google reCAPTCHA v2 */}
                                <div>
                                    <ReCAPTCHA
                                        sitekey="6LdWZmgqAAAAANm-U9o1q9EGk1srfrfkIv74ampz" // Replace with your site key
                                        onChange={handleCaptchaChange}
                                    />
                                </div>
                                {captchError && <FormHelperText error>Please Verify the ReCAPTCHA.</FormHelperText>}
                            </FormControl>
                            <Button
                                disabled={loading}
                                type="submit"
                                fullWidth
                                variant="contained"
                                onClick={validateInputs}
                                startIcon={loading ? <CircularProgress size={20} color="inherit" /> : null}
                                sx={{
                                    '&.Mui-disabled': {
                                    color: (theme) => theme.palette.primary.contrastText,
                                    }
                                }}
                            >
                                {loading ? 'Loading...' : 'Validate My Identity'}
                            </Button>
                        </Box>
                    </Card>

            </ValidateContainer>

            <MessageDialog
                open={showMessage}
                onClose={() => setShowMessage(false)}
                title="Validation Error"
                message={messageInfo?.message}
                severity="error"
                primaryAction={{
                    text: 'Ok',
                    handler: () => setShowMessage(false)
                }}
            />
        </>
    );
}
export default ValidateIdentity;